// Sprint 7C — owner-facing Booking AVAILABILITY editor (App Detail). The owner
// controls WHEN customers can book: booking window, weekly hours, closed days,
// blocked dates, blocked time ranges, and service duration. Server-backed: reads
// GET /v1/apps/:id/booking/availability and writes PATCH (owner-only via the
// gateway's app_metadata ownership check — a non-owner save returns 403). The
// customer app renders only the slots this config (minus existing bookings)
// produces. Honest loading / saved / error states; not a full calendar editor.
import { useCallback, useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { CalendarRange, Check, Loader2, AlertCircle, Plus, X, CreditCard } from 'lucide-preact';
import { t } from '../lib/i18n';

interface TimeRange { start: string; end: string }
interface DayAvailability { open: boolean; ranges: TimeRange[] }
interface BlockedTimeRange { date?: string; weekday?: number; start: string; end: string }
// §7C-HR — owner-selectable payment, stored INSIDE this availability config (no
// Supabase migration). Mirrors BookingPaymentConfig in packages/shared.
interface PaymentExternalLink { enabled: boolean; url: string; instructions: string; label: string }
interface PaymentPayAtBusiness { enabled: boolean; instructions: string }
interface PaymentConfig { externalLink: PaymentExternalLink; payAtBusiness: PaymentPayAtBusiness }
interface Config {
  windowDays: number; timezone: string; weekly: DayAvailability[];
  blockedDates: string[]; blockedTimeRanges: BlockedTimeRange[];
  serviceDurationMinutes: number; bufferMinutes: number; slotIntervalMinutes: number;
  payment: PaymentConfig;
}
const DEFAULT_PAYMENT: PaymentConfig = {
  externalLink: { enabled: false, url: '', instructions: '', label: '' },
  payAtBusiness: { enabled: false, instructions: '' },
};
// §S680 — weekday labels resolve through t() at render (keys, not literals).
const DAY_KEYS = ['mgmt.day.sun', 'mgmt.day.mon', 'mgmt.day.tue', 'mgmt.day.wed', 'mgmt.day.thu', 'mgmt.day.fri', 'mgmt.day.sat'];
const dayLabel = (i: number): string => t(DAY_KEYS[i] ?? 'mgmt.day.sun');
const WINDOWS = [7, 30, 90, 365];
// §LIFE-3 — timezone options: the creator's OWN detected zone first (so a
// non-US owner picks their real clock in one tap), then the current saved
// value, then a curated IANA list. De-duped; falls back gracefully.
const TZ_CURATED = [
  'Asia/Jerusalem', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Athens', 'Europe/Moscow',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Hong_Kong', 'Asia/Tokyo',
  'Australia/Sydney', 'America/Sao_Paulo', 'Africa/Johannesburg', 'UTC',
];
function tzOptions(current: string): string[] {
  let browser = '';
  try { browser = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { /* jsdom / old engine */ }
  const out: string[] = [];
  const seen = new Set<string>();
  for (const z of [browser, current, ...TZ_CURATED]) {
    if (z && !seen.has(z)) { seen.add(z); out.push(z); }
  }
  return out;
}
type Phase = { k: 'loading' } | { k: 'ready' } | { k: 'saving' } | { k: 'saved' } | { k: 'error'; msg: string } | { k: 'unavailable' };

export function BookingAvailability({ appId }: { appId: string }): JSX.Element {
  const [phase, setPhase] = useState<Phase>({ k: 'loading' });
  const [cfg, setCfg] = useState<Config | null>(null);
  const [newDate, setNewDate] = useState('');

  const load = useCallback(() => {
    setPhase({ k: 'loading' });
    fetch(`/v1/apps/${encodeURIComponent(appId)}/booking/availability`, { credentials: 'include' })
      .then(async (r) => {
        if (r.status === 503) return setPhase({ k: 'unavailable' });
        if (!r.ok) return setPhase({ k: 'error', msg: t('mgmt.av.errLoad') });
        const data = (await r.json()) as { config?: Config };
        // default payment if the server config predates §7C-HR / omits it
        if (data.config) setCfg({ ...data.config, payment: data.config.payment ?? DEFAULT_PAYMENT });
        setPhase({ k: 'ready' });
      })
      .catch(() => setPhase({ k: 'error', msg: t('mgmt.av.errLoad') }));
  }, [appId]);
  useEffect(() => { load(); }, [load]);

  const patch = (p: Partial<Config>): void => setCfg((c) => (c ? { ...c, ...p } : c));
  const patchExt = (d: Partial<PaymentExternalLink>): void =>
    setCfg((c) => (c ? { ...c, payment: { ...c.payment, externalLink: { ...c.payment.externalLink, ...d } } } : c));
  const patchPab = (d: Partial<PaymentPayAtBusiness>): void =>
    setCfg((c) => (c ? { ...c, payment: { ...c.payment, payAtBusiness: { ...c.payment.payAtBusiness, ...d } } } : c));
  const patchDay = (i: number, d: Partial<DayAvailability>): void =>
    setCfg((c) => (c ? { ...c, weekly: c.weekly.map((w, j) => (j === i ? { ...w, ...d } : w)) } : c));
  const setDayRange = (i: number, key: 'start' | 'end', val: string): void =>
    setCfg((c) => {
      if (!c) return c;
      const w = c.weekly[i];
      if (!w) return c;
      const range = (w.ranges[0] || { start: '09:00', end: '17:00' });
      return { ...c, weekly: c.weekly.map((x, j) => (j === i ? { ...x, ranges: [{ ...range, [key]: val }] } : x)) };
    });

  async function save(): Promise<void> {
    if (!cfg) return;
    setPhase({ k: 'saving' });
    try {
      const r = await fetch(`/v1/apps/${encodeURIComponent(appId)}/booking/availability`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'content-type': 'application/json' }, body: JSON.stringify({ config: cfg }),
      });
      if (r.status === 503) return setPhase({ k: 'unavailable' });
      if (r.status === 403) return setPhase({ k: 'error', msg: t('mgmt.av.err403') });
      if (r.status === 401) return setPhase({ k: 'error', msg: t('mgmt.av.err401') });
      if (!r.ok) return setPhase({ k: 'error', msg: t('mgmt.av.errSave') });
      const data = (await r.json()) as { config?: Config };
      if (data.config) setCfg({ ...data.config, payment: data.config.payment ?? DEFAULT_PAYMENT }); // reflect the server-normalized config
      setPhase({ k: 'saved' });
      setTimeout(() => setPhase((p) => (p.k === 'saved' ? { k: 'ready' } : p)), 2200);
    } catch { setPhase({ k: 'error', msg: t('mgmt.errConnShort') }); }
  }

  return (
    /* §MGMT-READINESS — same contract as ManageRecords (e63d014b): a section that fetches must publish
       its own state, because the panel-level data-statehost="manage" settles BEFORE this one does.
       Measured: with only the consoles reporting, an external observer still saw 37 vs 38 controls
       across identical runs — the one-control delta was this section's "Block date". */
    <section aria-label={t('mgmt.av.aria')} data-records-state={phase.k === 'loading' ? 'loading' : 'ready'} data-records-collection="availability" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="pf-row">
        <span className="pf-mgmt-panel__t" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <CalendarRange size={15} /> {t('mgmt.av.title')}
        </span>
      </div>

      {phase.k === 'loading' && <div className="skeleton-shimmer" style={{ height: 200, borderRadius: 'var(--r-lg)', border: '1px solid var(--border)' }} aria-hidden="true" />}
      {phase.k === 'unavailable' && (
        <div className="pf-mgmt-panel"><span className="pf-mgmt-panel__body"><span className="pf-mgmt-panel__t">{t('mgmt.av.unavailableT')}</span><span className="pf-mgmt-panel__s">{t('mgmt.av.unavailableS')}</span></span></div>
      )}

      {cfg && phase.k !== 'loading' && phase.k !== 'unavailable' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* booking window */}
          <div>
            <span style={lbl}>{t('mgmt.av.window')}</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              {WINDOWS.map((w) => (
                <button key={w} type="button" onClick={() => patch({ windowDays: w })}
                  className={`pf-btn pf-btn--sm${cfg.windowDays === w ? ' pf-btn--soft' : ''}`}>{w === 365 ? t('mgmt.av.year') : t('mgmt.av.days', { n: w })}</button>
              ))}
            </div>
          </div>

          {/* timezone — §LIFE-3: owner control so bookings, hours and "today" use
              the business's REAL clock, not a hardcoded America/New_York (which
              rejected legitimate local slots as "past" for non-US owners). The
              creator's own detected zone is offered first for easy self-select. */}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={lbl}>{t('mgmt.av.timezone')}</span>
            <select className="pf-input" style={{ ...timeInput, width: 'auto', minWidth: 220 }} value={cfg.timezone}
              onChange={(e) => patch({ timezone: (e.target as HTMLSelectElement).value })}>
              {tzOptions(cfg.timezone).map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
            </select>
            <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{t('mgmt.av.timezoneHint')}</span>
          </label>

          {/* weekly hours */}
          <div>
            <span style={lbl}>{t('mgmt.av.weekly')}</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
              {cfg.weekly.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: 92, fontSize: 13, fontWeight: 600, color: 'var(--fg)' }}>
                    <input type="checkbox" checked={d.open} onChange={(e) => patchDay(i, { open: (e.target as HTMLInputElement).checked, ranges: (e.target as HTMLInputElement).checked && d.ranges.length === 0 ? [{ start: '09:00', end: '17:00' }] : d.ranges })} />
                    {dayLabel(i)}
                  </label>
                  {d.open ? (
                    <>
                      <input type="time" value={d.ranges[0]?.start || '09:00'} onInput={(e) => setDayRange(i, 'start', (e.target as HTMLInputElement).value)} className="pf-input" style={timeInput} />
                      <span style={{ color: 'var(--fg-muted)' }}>–</span>
                      <input type="time" value={d.ranges[0]?.end || '17:00'} onInput={(e) => setDayRange(i, 'end', (e.target as HTMLInputElement).value)} className="pf-input" style={timeInput} />
                    </>
                  ) : <span style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>{t('mgmt.av.closed')}</span>}
                </div>
              ))}
            </div>
          </div>

          {/* service duration */}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={lbl}>{t('mgmt.av.duration')}</span>
            <input type="number" min={5} max={600} step={5} value={cfg.serviceDurationMinutes} onInput={(e) => patch({ serviceDurationMinutes: Number((e.target as HTMLInputElement).value) || 60 })} className="pf-input" style={{ ...timeInput, width: 110 }} />
          </label>

          {/* blocked dates */}
          <div>
            <span style={lbl}>{t('mgmt.av.blockedDates')}</span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
              <input type="date" value={newDate} onInput={(e) => setNewDate((e.target as HTMLInputElement).value)} className="pf-input" style={timeInput} />
              <button type="button" className="pf-btn pf-btn--sm" disabled={!newDate} onClick={() => { if (newDate && !cfg.blockedDates.includes(newDate)) patch({ blockedDates: [...cfg.blockedDates, newDate].sort() }); setNewDate(''); }}><Plus size={13} /> {t('mgmt.av.blockDate')}</button>
            </div>
            {cfg.blockedDates.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                {cfg.blockedDates.map((d) => (
                  <span key={d} className="pf-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>{d}
                    <button type="button" aria-label={t('mgmt.av.unblock', { d })} onClick={() => patch({ blockedDates: cfg.blockedDates.filter((x) => x !== d) })} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, display: 'inline-flex', color: 'var(--fg-muted)' }}><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* blocked weekday time ranges (e.g. lunch) */}
          <BlockedTimes cfg={cfg} patch={patch} />

          {/* §7C-HR — owner-selectable payment options. Both are off by default;
              the owner may enable either, both, or neither. Pomela never takes the
              money or verifies it — the owner confirms payment manually. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 4, borderTop: '1px solid var(--border)' }}>
            <span style={{ ...lbl, display: 'inline-flex', alignItems: 'center', gap: 6 }}><CreditCard size={14} /> {t('mgmt.av.payment')}</span>
            <span style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: -4 }}>{t('mgmt.av.payNote')}</span>

            {/* external payment link */}
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: 'var(--fg)' }}>
              <input type="checkbox" checked={cfg.payment.externalLink.enabled} onChange={(e) => patchExt({ enabled: (e.target as HTMLInputElement).checked })} />
              {t('mgmt.av.extEnable')}
            </label>
            {cfg.payment.externalLink.enabled && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingInlineStart: 24 }}>
                <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>{t('mgmt.av.extNote')}</span>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={subLbl}>{t('mgmt.av.extUrl')}</span>
                  <input type="url" inputMode="url" placeholder="https://" value={cfg.payment.externalLink.url} onInput={(e) => patchExt({ url: (e.target as HTMLInputElement).value })} className="pf-input" style={{ ...timeInput, width: '100%' }} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={subLbl}>{t('mgmt.av.extLabel')}</span>
                  <input type="text" placeholder={t('mgmt.av.extLabelPh')} value={cfg.payment.externalLink.label} onInput={(e) => patchExt({ label: (e.target as HTMLInputElement).value })} className="pf-input" style={{ ...timeInput, width: '100%' }} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={subLbl}>{t('mgmt.av.instructions')}</span>
                  <textarea rows={2} placeholder={t('mgmt.av.extInstrPh')} value={cfg.payment.externalLink.instructions} onInput={(e) => patchExt({ instructions: (e.target as HTMLTextAreaElement).value })} className="pf-input" style={{ ...timeInput, width: '100%', resize: 'vertical' }} />
                </label>
              </div>
            )}

            {/* pay at the business */}
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: 'var(--fg)' }}>
              <input type="checkbox" checked={cfg.payment.payAtBusiness.enabled} onChange={(e) => patchPab({ enabled: (e.target as HTMLInputElement).checked })} />
              {t('mgmt.av.pabEnable')}
            </label>
            {cfg.payment.payAtBusiness.enabled && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingInlineStart: 24 }}>
                <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>{t('mgmt.av.pabNote')}</span>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={subLbl}>{t('mgmt.av.instructions')}</span>
                  <textarea rows={2} placeholder={t('mgmt.av.pabInstrPh')} value={cfg.payment.payAtBusiness.instructions} onInput={(e) => patchPab({ instructions: (e.target as HTMLTextAreaElement).value })} className="pf-input" style={{ ...timeInput, width: '100%', resize: 'vertical' }} />
                </label>
              </div>
            )}
          </div>

          {phase.k === 'error' && <p style={{ fontSize: 12.5, color: 'var(--color-danger)', margin: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}><AlertCircle size={13} /> {phase.msg}</p>}

          <div className="pf-row">
            <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>{t('mgmt.av.footNote')}</span>
            <button type="button" className="pf-btn pf-btn--grad pf-btn--sm" disabled={phase.k === 'saving'} onClick={() => void save()}>
              {phase.k === 'saving' ? <><Loader2 size={13} className="pf-spin" /> {t('mgmt.saving')}</> : phase.k === 'saved' ? <><Check size={13} /> {t('mgmt.saved')}</> : t('mgmt.av.save')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// a minimal blocked-time-range editor (weekday + start–end, e.g. a daily lunch break)
function BlockedTimes({ cfg, patch }: { cfg: Config; patch: (p: Partial<Config>) => void }): JSX.Element {
  const [wd, setWd] = useState(1);
  const [start, setStart] = useState('12:00');
  const [end, setEnd] = useState('13:00');
  return (
    <div>
      <span style={lbl}>{t('mgmt.av.blockedTimes')}</span>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
        <select value={wd} onChange={(e) => setWd(Number((e.target as HTMLSelectElement).value))} className="pf-input" style={timeInput}>
          {DAY_KEYS.map((k, i) => <option key={i} value={i}>{t(k)}</option>)}
        </select>
        <input type="time" value={start} onInput={(e) => setStart((e.target as HTMLInputElement).value)} className="pf-input" style={timeInput} />
        <span style={{ color: 'var(--fg-muted)' }}>–</span>
        <input type="time" value={end} onInput={(e) => setEnd((e.target as HTMLInputElement).value)} className="pf-input" style={timeInput} />
        <button type="button" className="pf-btn pf-btn--sm" onClick={() => patch({ blockedTimeRanges: [...cfg.blockedTimeRanges, { weekday: wd, start, end }] })}><Plus size={13} /> {t('mgmt.av.block')}</button>
      </div>
      {cfg.blockedTimeRanges.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {cfg.blockedTimeRanges.map((b, i) => (
            <span key={i} className="pf-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              {(typeof b.weekday === 'number' ? dayLabel(b.weekday) : b.date)} {b.start}–{b.end}
              <button type="button" aria-label={t('mgmt.av.removeBlocked')} onClick={() => patch({ blockedTimeRanges: cfg.blockedTimeRanges.filter((_, j) => j !== i) })} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, display: 'inline-flex', color: 'var(--fg-muted)' }}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const lbl: JSX.CSSProperties = { fontSize: 12.5, fontWeight: 700, color: 'var(--fg)' };
const subLbl: JSX.CSSProperties = { fontSize: 12, fontWeight: 600, color: 'var(--fg)' };
const timeInput: JSX.CSSProperties = { padding: '7px 9px', borderRadius: 'var(--r-md)', border: '1px solid var(--border)', background: 'var(--bg-el)', color: 'var(--fg)', fontSize: 13 };
